import type { ReadonlyDeep } from 'type-fest';

import type { Locale } from '@change-corgi/config/locales';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { HomepageData, type HomepageDataQuery, type HomepageDataQueryVariables } from './api.graphql';
import { getTopImpactStories } from './getTopImpactStories';
import type { ImpactStoryFCMData, TopImpactStoryPetitionData } from './types';
import { HomepagePageDataError } from './types';

export type HomepagePageData = ReadonlyDeep<{
	totalUserCount: HomepageDataQuery['totalUserCount'];
	topImpactStories: Array<TopImpactStoryPetitionData & ImpactStoryFCMData>;
	tips: ReadonlyArray<{
		title: string;
		guideLink: string;
	}>;
}>;

export async function getHomepagePageData(
	utilityContext: UtilityContext,
	{ locale }: { locale: string },
): Promise<HomepagePageData> {
	const {
		gql: { fetch },
		errorReporter,
	} = utilityContext;
	const { data } = await fetch<HomepageDataQuery, HomepageDataQueryVariables>({
		query: HomepageData,
		rejectOnError: false,
	});

	if (!data) {
		// No data returned
		const error = new HomepagePageDataError('homepage_page_data_not_found');
		void errorReporter.report(error);
		throw error;
	}

	if (!data.featureConfigs.capybaraHomepageV3) {
		// No featureConfigs data
		const error = new HomepagePageDataError('homepage_page_data_not_found', {
			message: 'No featureConfigs data',
		});
		void errorReporter.report(error);
		throw error;
	}

	const {
		featureConfigs: { capybaraHomepageV3 },
	} = data;
	const localeConfig = capybaraHomepageV3.perLocaleConfig?.[locale as Locale];

	const topImpactStories = localeConfig?.topImpactStories || [];

	// Build impact story by grabbing petition data and
	// merging it with the FCM data
	const topImpactStoriesPetitions = await Promise.all(
		topImpactStories.map(async (story) => {
			const petitionData = await getTopImpactStories(utilityContext, {
				locale,
				petitionId: story.petitionId,
			});

			return {
				...petitionData,
				summary: story.summary,
				imageUrl: story.imageUrl,
				badgeTitle: story.badgeTitle ?? '',
				testimonialUrl: story.testimonialUrl ?? '',
			};
		}),
	);

	return {
		totalUserCount: data.totalUserCount,
		topImpactStories: topImpactStoriesPetitions,
		tips: localeConfig?.tips || [],
	};
}
